import { useState } from "react";
import { LilyPad } from "./LilyPad";
import { JoinRequestLily } from "./JoinRequestLily";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface Habit {
  id: string;
  name: string;
  frog: {
    stage: "egg" | "tadpole" | "young" | "adult" | "hibernating";
    daysCompleted: number;
    currentStreak: number;
    positionX: number;
    positionY: number;
  };
}

interface JoinRequest {
  id: string;
  userName?: string;
  createdAt: string;
}

interface PondSceneProps {
  habits: Habit[];
  joinRequests?: JoinRequest[];
  isOwner?: boolean;
  onHabitClick: (habitId: string) => void;
  onAddHabit: () => void;
  onApproveRequest?: (requestId: string) => Promise<boolean>;
  onRejectRequest?: (requestId: string) => Promise<boolean>;
}

export const PondScene = ({
  habits,
  joinRequests = [],
  isOwner = false,
  onHabitClick,
  onAddHabit,
  onApproveRequest,
  onRejectRequest,
}: PondSceneProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [draggingRequestId, setDraggingRequestId] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<JoinRequest | null>(null);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const requestId = e.dataTransfer.getData("requestId");
    if (requestId && onApproveRequest) {
      await onApproveRequest(requestId);
    }
  };

  const handleRequestClick = (request: JoinRequest) => {
    setSelectedRequest(request);
    setInfoDialogOpen(true);
  };

  // Position join requests around the pond edges
  const getRequestPosition = (index: number, total: number) => {
    // Distribute around the edges
    const positions = [
      { x: -5, y: 20 },
      { x: -5, y: 50 },
      { x: -5, y: 80 },
      { x: 105, y: 20 },
      { x: 105, y: 50 },
      { x: 105, y: 80 },
      { x: 30, y: -5 },
      { x: 60, y: -5 },
    ];
    return positions[index % positions.length];
  };

  return (
    <>
      <div className="relative w-full h-full rounded-3xl overflow-visible">
        {/* Join requests outside the pond */}
        {isOwner && joinRequests.length > 0 && (
          <div className="absolute inset-0 pointer-events-none">
            {joinRequests.map((request, index) => {
              const pos = getRequestPosition(index, joinRequests.length);
              return (
                <div
                  key={request.id}
                  className="absolute pointer-events-auto"
                  style={{
                    left: `${pos.x}%`,
                    top: `${pos.y}%`,
                    transform: "translate(-50%, -50%)",
                    zIndex: 60,
                  }}
                >
                  <JoinRequestLily
                    request={{
                      id: request.id,
                      userName: request.userName || "Usuário",
                      createdAt: request.createdAt,
                    }}
                    onDragStart={setDraggingRequestId}
                    onClick={() => handleRequestClick(request)}
                    className="animate-float"
                    style={{ animationDelay: `${index * 0.3}s` }}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Pond container */}
        <div
          className={cn(
            "relative w-full h-full rounded-3xl overflow-hidden shadow-float transition-all duration-300",
            isDragOver && "ring-4 ring-coral ring-opacity-50 scale-[1.02]"
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Drop zone indicator */}
          {isDragOver && (
            <div className="absolute inset-0 z-40 bg-coral/20 flex items-center justify-center pointer-events-none">
              <div className="bg-background/90 rounded-2xl px-6 py-4 shadow-lg">
                <p className="text-lg font-display font-bold text-coral">
                  Solte para aprovar! 🐸
                </p>
              </div>
            </div>
          )}

          {/* Pond background */}
          <div className="absolute inset-0 bg-pond-gradient">
            {/* Water texture */}
            <div className="absolute inset-0 opacity-30">
              <div className="absolute top-1/4 left-1/4 w-40 h-40 bg-pond-light/30 rounded-full blur-3xl" />
              <div className="absolute top-1/2 right-1/4 w-32 h-32 bg-teal-light/30 rounded-full blur-3xl" />
              <div className="absolute bottom-1/4 left-1/3 w-36 h-36 bg-pond-surface/30 rounded-full blur-3xl" />
            </div>

            {/* Animated water ripples */}
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-20 h-20 border border-primary-foreground/10 rounded-full water-ripple"
                  style={{
                    left: `${20 + i * 15}%`,
                    top: `${30 + (i % 3) * 20}%`,
                    animationDelay: `${i * 0.5}s`,
                  }}
                />
              ))}
            </div>

            {/* Decorative plants at edges */}
            <div className="absolute bottom-0 left-0 w-32 h-40">
              <div className="absolute bottom-0 left-4 w-2 h-20 bg-lily-pad/60 rounded-t-full transform -rotate-12" />
              <div className="absolute bottom-0 left-8 w-2 h-24 bg-lily-pad/80 rounded-t-full" />
              <div className="absolute bottom-0 left-12 w-2 h-16 bg-lily-pad/60 rounded-t-full transform rotate-12" />
            </div>
            <div className="absolute bottom-0 right-0 w-32 h-40">
              <div className="absolute bottom-0 right-4 w-2 h-20 bg-lily-pad/60 rounded-t-full transform rotate-12" />
              <div className="absolute bottom-0 right-8 w-2 h-28 bg-lily-pad/80 rounded-t-full" />
              <div className="absolute bottom-0 right-12 w-2 h-18 bg-lily-pad/60 rounded-t-full transform -rotate-12" />
            </div>
          </div>

          {/* Lily pads with frogs */}
          <div className="relative w-full h-full">
            {habits.map((habit) => (
              <LilyPad
                key={habit.id}
                habitName={habit.name}
                frogStage={habit.frog.stage}
                daysCompleted={habit.frog.daysCompleted}
                currentStreak={habit.frog.currentStreak}
                onClick={() => onHabitClick(habit.id)}
                className="absolute animate-float"
                style={{
                  left: `${habit.frog.positionX}%`,
                  top: `${habit.frog.positionY}%`,
                  animationDelay: `${Math.random() * 2}s`,
                }}
              />
            ))}

            {/* Add new habit button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAddHabit();
              }}
              className={cn(
                "absolute bottom-8 right-8 w-16 h-16 rounded-full z-50",
                "bg-coral text-white shadow-float",
                "flex items-center justify-center cursor-pointer",
                "hover:scale-110 hover:shadow-glow transition-all duration-300",
                "active:scale-95"
              )}
            >
              <Plus size={28} strokeWidth={3} />
            </button>
          </div>

          {/* Empty state */}
          {habits.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-primary-foreground/80">
                <div className="text-6xl mb-4">🌿</div>
                <h3 className="text-xl font-display font-bold mb-2">
                  Sua lagoa está vazia
                </h3>
                <p className="text-sm opacity-80">
                  Adicione seu primeiro hábito e veja seu sapo crescer!
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Join requests hint */}
        {isOwner && joinRequests.length > 0 && (
          <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-center">
            <p className="text-xs text-muted-foreground bg-background/80 px-3 py-1 rounded-full">
              {joinRequests.length} solicitação(ões) pendente(s) • Arraste para a lagoa para aprovar
            </p>
          </div>
        )}
      </div>

      {/* Request info dialog */}
      <Dialog open={infoDialogOpen} onOpenChange={setInfoDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>🥚</span> Solicitação de Entrada
            </DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-xl">
                <div className="w-12 h-12 rounded-full bg-lily-pad flex items-center justify-center text-2xl">
                  🐸
                </div>
                <div>
                  <p className="font-display font-bold">
                    {selectedRequest.userName || "Usuário"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Quer entrar na sua lagoa
                  </p>
                </div>
              </div>

              <p className="text-sm text-muted-foreground text-center">
                Arraste a vitória régia para dentro da lagoa para aprovar,
                ou use os botões abaixo.
              </p>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={async () => {
                    if (onRejectRequest) {
                      await onRejectRequest(selectedRequest.id);
                    }
                    setInfoDialogOpen(false);
                  }}
                >
                  Recusar
                </Button>
                <Button
                  variant="coral"
                  className="flex-1"
                  onClick={async () => {
                    if (onApproveRequest) {
                      await onApproveRequest(selectedRequest.id);
                    }
                    setInfoDialogOpen(false);
                  }}
                >
                  Aprovar 🐸
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
